import type { NavigateFunction } from "react-router-dom";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fetchRequestWithAuth } from "@/lib/api-client";
import { getAdminImagesUploadUrl } from "@/lib/config";
import { buildUpdateEventPayloadFromEvent } from "@/lib/event-utils";
import { adminQueryKeys, useAdminEvent, useAdminEventUpdate } from "@/lib/admin-api";
import type { ToastMessage } from "@/lib/ui";
import { useEventState } from "./hooks/useEventState";
import { useEventSessions } from "./hooks/useEventSessions";
import { useEventPricePoints } from "./hooks/useEventPricePoints";
import type { AdminEvent, AdminEventPricePoint, AdminEventSession } from "./types";

type SessionDraft = {
  startsAt: Date | null;
  endsAt: Date | null;
  label: string;
};

type PricePointDraft = {
  label: string;
  description: string;
  priceCents: number;
  currency: string;
  sortOrder: number;
  availableFrom: Date | null;
  availableUntil: Date | null;
  depositCents: number | null;
  balanceDueAt: Date | null;
  capacity: number | null;
};

type UseEventDetailModel = {
  event: AdminEvent | null;
  loading: boolean;

  sessionDialogVisible: boolean;
  setSessionDialogVisible: Dispatch<SetStateAction<boolean>>;
  pricePointDialogVisible: boolean;
  setPricePointDialogVisible: Dispatch<SetStateAction<boolean>>;

  editingSession: AdminEventSession | null;
  editingPricePoint: AdminEventPricePoint | null;

  newSession: SessionDraft;
  setNewSession: Dispatch<SetStateAction<SessionDraft>>;
  newPricePoint: PricePointDraft;
  setNewPricePoint: Dispatch<SetStateAction<PricePointDraft>>;

  refreshEvent: () => Promise<void>;

  handlePublish: () => Promise<void>;
  handleUnpublish: () => Promise<void>;
  handleCancel: () => Promise<void>;
  handleUncancel: () => Promise<void>;

  handleMovePricePointUp: (pricePointId: string) => Promise<void>;
  handleMovePricePointDown: (pricePointId: string) => Promise<void>;

  handleAddSession: () => void;
  handleEditSession: (session: AdminEventSession) => void;
  handleRemoveSession: (sessionId: string) => void;
  handleSaveSession: () => Promise<void>;

  handleAddPricePoint: () => void;
  handleEditPricePoint: (pricePoint: AdminEventPricePoint) => void;
  handleRemovePricePoint: (pricePointId: string) => void;
  handleSavePricePoint: () => Promise<void>;

  handleUploadFile: (file: File) => Promise<void>;

  formatPrice: (cents: number, currency: string) => string;
};

export function useEventDetailModel(args: {
  id: string | undefined;
  navigate: NavigateFunction;
  showToast: (message: ToastMessage) => void;
}): UseEventDetailModel {
  const { id, navigate, showToast } = args;

  const queryClient = useQueryClient();
  const eventQuery = useAdminEvent(id);
  const updateMutation = useAdminEventUpdate();

  const event: AdminEvent | null = eventQuery.data?.event ?? null;
  const loading = eventQuery.isLoading;

  const refreshEvent = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: adminQueryKeys.events.detail,
    });
  }, [queryClient]);

  // Route guard: missing id
  useEffect(() => {
    if (!id) {
      void navigate("/events");
    }
  }, [id, navigate]);

  // Handle fetch failures / missing event (best-effort UX parity with the old implementation).
  const hasNavigatedAwayRef = useRef(false);
  useEffect(() => {
    if (hasNavigatedAwayRef.current) return;

    if (eventQuery.isError) {
      hasNavigatedAwayRef.current = true;
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to load event",
      });
      void navigate("/events");
      return;
    }

    if (eventQuery.isSuccess && !eventQuery.isFetching && id && !eventQuery.data?.event) {
      hasNavigatedAwayRef.current = true;
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Event not found",
      });
      void navigate("/events");
    }
  }, [eventQuery.data, eventQuery.isError, eventQuery.isFetching, eventQuery.isSuccess, id, navigate, showToast]);

  const eventState = useEventState({
    event,
    eventId: id,
    refreshEvent,
    showToast,
  });

  const sessions = useEventSessions({
    event,
    eventId: id,
    refreshEvent,
    showToast,
  });

  const pricePoints = useEventPricePoints({
    event,
    eventId: id,
    refreshEvent,
    showToast,
  });

  const handleUploadFile = useCallback(
    async (file: File) => {
      if (!id || !event) return;

      try {
        const uploadUrl = getAdminImagesUploadUrl();

        const uploadHeaders = new Headers();
        uploadHeaders.set("Content-Type", file.type || "image/jpeg");

        const uploadRequest = new Request(uploadUrl, {
          method: "POST",
          headers: uploadHeaders,
          body: file,
        });
        const uploadResponse = await fetchRequestWithAuth(uploadRequest);

        if (!uploadResponse.ok) {
          throw new Error("Failed to upload image file");
        }

        const { imageUrl } = (await uploadResponse.json()) as {
          imageUrl: string;
        };

        const payload = buildUpdateEventPayloadFromEvent(event, {
          imageUrl,
        });

        await updateMutation.mutateAsync({
          params: { path: { eventId: id } },
          body: payload,
        });

        showToast({
          severity: "success",
          summary: "Success",
          detail: "Image uploaded and event updated successfully",
        });

        await refreshEvent();
      } catch (error) {
        showToast({
          severity: "error",
          summary: "Error",
          detail: error instanceof Error ? error.message : "Failed to upload image",
        });
      }
    },
    [event, id, refreshEvent, showToast, updateMutation],
  );

  const formatPrice = useCallback((cents: number, currency: string) => {
    if (cents === 0) return "Free";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  }, []);

  return {
    event,
    loading,

    sessionDialogVisible: sessions.sessionDialogVisible,
    setSessionDialogVisible: sessions.setSessionDialogVisible,
    pricePointDialogVisible: pricePoints.pricePointDialogVisible,
    setPricePointDialogVisible: pricePoints.setPricePointDialogVisible,

    editingSession: sessions.editingSession,
    editingPricePoint: pricePoints.editingPricePoint,

    newSession: sessions.newSession,
    setNewSession: sessions.setNewSession,
    newPricePoint: pricePoints.newPricePoint,
    setNewPricePoint: pricePoints.setNewPricePoint,

    refreshEvent,

    handlePublish: eventState.handlePublish,
    handleUnpublish: eventState.handleUnpublish,
    handleCancel: eventState.handleCancel,
    handleUncancel: eventState.handleUncancel,

    handleMovePricePointUp: pricePoints.handleMovePricePointUp,
    handleMovePricePointDown: pricePoints.handleMovePricePointDown,

    handleAddSession: sessions.handleAddSession,
    handleEditSession: sessions.handleEditSession,
    handleRemoveSession: sessions.handleRemoveSession,
    handleSaveSession: sessions.handleSaveSession,

    handleAddPricePoint: pricePoints.handleAddPricePoint,
    handleEditPricePoint: pricePoints.handleEditPricePoint,
    handleRemovePricePoint: pricePoints.handleRemovePricePoint,
    handleSavePricePoint: pricePoints.handleSavePricePoint,

    handleUploadFile,

    formatPrice,
  };
}
